import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import axiosAuthInstance from '../../../utils/axios/axiosAuthInstance'
import { Field, Formik, FormikProvider, useFormik } from 'formik'
import CustomInput from '../../../components/common/CustomInput'
import CustomFile from '../../../components/common/CustomFile'
import * as Yup from 'yup'
import CustomTextarea from '../../../components/common/CustomTextarea'
import { FiEye, FiEyeOff } from 'react-icons/fi'
import CustomSelect from '../../../components/common/CustomSelect'

// Validation

const validationSchema = Yup.object({
  tagline: Yup.string().trim().required('Tagline is required'),
  logo: Yup.string().trim().required('Light Logo is Required'),
  darkLogo: Yup.string().trim().required('Dark Logo is Required'),
  about: Yup.string().trim().required('About information is required'),
  history: Yup.string().trim().required('History is required'),
  vision: Yup.string().trim().required('Vision is required'),
  mission: Yup.string().trim().required('Mission is required'),
  contact: Yup.string().trim().required('Contact information is required'),
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
  website: Yup.string().trim().required('website is required'),
  username: Yup.string().trim().required('Username is required'),
  password: Yup.string().required('Password is required'),
  confirm_password: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
    .required('Confirm Password is required'),
})

const Add = () => {
  const { id } = useParams()

  const [step, setStep] = useState(1)
  const [stepInfo, setStepInfo] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [allUsers, setAllUsers] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [seriesOptions, setSeriesOptions] = useState([])

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword)
  }

  const navigate = useNavigate()

  useEffect(() => {
    switch (step) {
      case 1:
        setStepInfo('Company Branding Details')
        break

      case 2:
        setStepInfo('Social Media Links')
        break

      case 3:
        setStepInfo('Catalogue Details')
        break

      case 4:
        setStepInfo('Create Credentials')
        break

      default:
        setStepInfo('')
    }
  }, [step])

  const nextStep = () => {
    setStep((prev) => (prev < 4 ? prev + 1 : prev))
  }

  const prevStep = () => {
    setStep((prev) => (prev > 1 ? prev - 1 : prev))
  }
  const fetchAllUsers = async () => {
    try {
      const response = await axiosAuthInstance.get('team/dropdown', {
        isAll: true,
      })
      if (response.status === 200) {
        const transformedData = response?.data?.result?.map((item) => ({
          value: item._id,
          label: `${item.full_name} (${item.role})`,
        }))
        setAllUsers(transformedData)
      }
    } catch (error) {
      console.log('error', error)
    }
  }

  const getSeries = async () => {
    try {
      const response = await axiosAuthInstance.get(`series/dropdown/${id}`)
      if (response.status === 200) {
        const transformedData = response?.data?.result?.map((item) => ({
          value: item._id,
          label: `${item.name}`,
        }))
        setSeriesOptions(transformedData)
      }
    } catch (error) {
      console.log('error', error)
    }
  }

  useEffect(() => {
    fetchAllUsers()
    getSeries()
  }, [])

  const handleSubmit = async (values) => {
    setIsLoading(true)
    try {
      const formData = new FormData()

      Object.keys(values).forEach((key) => {
        if (
          key !== 'logo' &&
          key !== 'darkLogo' &&
          key !== 'series' &&
          key !== 'sm_post_user' &&
          key !== 'sm_reel_user' &&
          key !== 'sm_walk_user'
        ) {
          formData.append(key, values[key])
        }
      })

      if (values?.sm_post_user?.length > 0) {
        formData.append('sm_post_user', JSON.stringify(values.sm_post_user))
      }

      if (values?.sm_reel_user?.length > 0) {
        formData.append('sm_reel_user', JSON.stringify(values.sm_reel_user))
      }

      if (values?.sm_walk_user?.length > 0) {
        formData.append('sm_walk_user', JSON.stringify(values.sm_walk_user))
      }

      if (values?.series?.length > 0) {
        formData.append('series', JSON.stringify(values.series))
      }

      // Handle single file fields
      if (values.logo) {
        formData.append('logo', values.logo)
      }

      if (values.darkLogo) {
        formData.append('darkLogo', values.darkLogo)
      }

      const response = await axiosAuthInstance.post(
        'company-branding/add',
        formData,
      )

      if (response.status === 200) {
        navigate('/company-branding')
      }
    } catch (error) {
      console.log(error)
    } finally {
      setIsLoading(false)
    }
  }
  const formik = useFormik({
    initialValues: {
      company: id,
      logo: null,
      darkLogo: null,
      tagline: '',
      about: '',
      history: '',
      vision: '',
      mission: '',
      contact: '',
      email: '',
      facebook: '',
      twitter: '',
      linkedin: '',
      instagram: '',
      website: '',
      series: [],
      username: '',
      password: '',
      confirm_password: '',
      is_sm: false,
      sm_post: '',
      sm_post_user: [],
      sm_reel: '',
      sm_reel_user: [],
      sm_walk: '',
      sm_walk_user: [],
    },
    validationSchema: validationSchema,
    onSubmit: async (values, formikHelpers) => {
      const error = await formik.validateForm()
      if (Object.keys(error).length === 0) {
        handleSubmit(values, formikHelpers)
      } else {
        formikHelpers.setTouched(error)
      }
    },
  })

  return (
    <FormikProvider value={formik}>
      <form className='flex flex-col p-4 gap-4' onSubmit={formik.handleSubmit}>
        <div>
          <label className='bg-blue-100 text-orange-500 rounded-full text-l font-medium me-2 px-2.5 py-0.5 dark:bg-orange-500 dark:text-white'>
            Step: {step}
          </label>
          {stepInfo && (
            <label className='bg-blue-100 text-orange-500 rounded-full text-l font-medium me-2 px-2.5 py-0.5 dark:bg-orange-500 dark:text-white'>
              {stepInfo}
            </label>
          )}
        </div>

        {step === 1 && (
          <>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <Field
                name='logo'
                accept='image/*'
                label='Upload Logo (Light)'
                required
                component={CustomFile}
              />

              <Field
                name='darkLogo'
                accept='image/*'
                label='Dark Logo'
                required
                component={CustomFile}
              />
            </div>
            <Field
              name='tagline'
              label='Tagline'
              placeholder='Tagline'
              required
              component={CustomInput}
            />

            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <Field
                name='about'
                label='About'
                placeholder='About'
                required
                component={CustomTextarea}
              />

              <Field
                name='history'
                label='History'
                placeholder='History'
                required
                component={CustomTextarea}
              />
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <Field
                name='mission'
                label='Mission'
                placeholder='Mission'
                required
                component={CustomTextarea}
              />

              <Field
                name='vision'
                label='Vision'
                placeholder='Vision'
                required
                component={CustomTextarea}
              />
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <Field
                name='contact'
                label='Contact'
                placeholder='Contact'
                required
                component={CustomInput}
              />

              <Field
                name='email'
                label='Email'
                placeholder='Email'
                required
                component={CustomInput}
              />
            </div>
            <Field
              name='website'
              label='Website'
              placeholder='Website'
              required
              component={CustomInput}
            />
            {/* <Field
              name="is_sm"
              label="Social Media"
              placeholder="Social Media"
              component={CustomCheckBox}
            />
            {formik.values.is_sm && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field
                    name="sm_post"
                    label="Social Media Post"
                    type="Number"
                    placeholder="Social Media Post"
                    component={CustomInput}
                  />
                  <Field
                    name="sm_post_user"
                    label="Social Media Post User"
                    placeholder="Social Media Post User"
                    component={CustomSelect}
                    options={allUsers}
                    isMulti={true}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field
                    name="sm_reel"
                    label="Social Media Reel"
                    placeholder="Social Media Reel"
                    type="Number"
                    component={CustomInput}
                  />
                  <Field
                    name="sm_reel_user"
                    label="Social Media Reel User"
                    placeholder="Social Media Reel User"
                    component={CustomSelect}
                    options={allUsers}
                    isMulti={true}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field
                    name="sm_walk"
                    label="Social Media Walk Through"
                    placeholder="Social  Walk Through"
                    type="Number"
                    component={CustomInput}
                  />
                  <Field
                    name="sm_walk_user"
                    label="Social Media Walk Through User"
                    placeholder="Social Media Walk Through User"
                    component={CustomSelect}
                    options={allUsers}
                    isMulti={true}
                  />
                </div>
              </>
            )} */}
          </>
        )}

        {step === 2 && (
          <>
            <Field
              name='facebook'
              label='Facebook'
              placeholder='Facebook'
              component={CustomInput}
            />

            <Field
              name='twitter'
              label='Twitter'
              placeholder='Twitter'
              component={CustomInput}
            />

            <Field
              name='linkedin'
              label='Linkedin'
              placeholder='Linkedin'
              component={CustomInput}
            />

            <Field
              name='instagram'
              label='Instagram'
              placeholder='Instagram'
              component={CustomInput}
            />
          </>
        )}

        {step === 3 && (
          <>
            <Field
              name="series"
              label="Series"
              placeholder="Series"
              component={CustomSelect}
              options={seriesOptions}
              isMulti
            />
          </>
        )}

        {step === 4 && (
          <>
            <Field
              name='username'
              label='Username'
              placeholder='Enter Username'
              required
              component={CustomInput}
            />

            <div className='relative'>
              <Field
                name='password'
                label='Password'
                placeholder='Enter Password'
                required
                component={CustomInput}
                type={showPassword ? 'text' : 'password'}
              />
              <div
                className='absolute inset-y-0 pt-5 right-4 flex items-center cursor-pointer'
                onClick={togglePasswordVisibility}
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </div>
            </div>

            <div className='relative'>
              <Field
                name='confirm_password'
                label='Confirm Password'
                placeholder='Enter Confirm Password'
                required
                component={CustomInput}
                type={showConfirmPassword ? 'text' : 'password'}
              />
              <div
                className='absolute inset-y-0 pt-5 right-4 flex items-center cursor-pointer'
                onClick={toggleConfirmPasswordVisibility}
              >
                {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
              </div>
            </div>
          </>
        )}

        <div className='flex justify-end'>
          {step !== 1 && (
            <button
              type='button'
              onClick={prevStep}
              className='bg-gray-500 hover:bg-white text-xl font-medium mx-2 text-white hover:text-gray-500 border border-gray-500 rounded-lg px-6 py-2'
            >
              Previous
            </button>
          )}

          {step !== 4 && (
            <button
              type='button'
              onClick={nextStep}
              className='bg-primary hover:bg-white text-xl font-medium text-white hover:text-primary border border-gray-500 rounded-lg px-6 py-2'
            >
              Next
            </button>
          )}

          {step === 4 && (
            <button
              type='submit'
              disabled={isLoading}
              className='bg-primary hover:bg-white text-xl font-medium text-white hover:text-primary border border-gray-500 rounded-lg px-6 py-2'
            >
              {isLoading ? 'Submitting...' : 'Submit'}
            </button>
          )}
        </div>
      </form>
    </FormikProvider>
  )
}

export default Add
